import React, { useState, useRef } from "react";
import { ZoomIn, ZoomOut, RotateCcw, Download } from "lucide-react";
import { toast } from "sonner";

interface WorkflowDiagramProps {
  chain: Array<{ id: string; name: string }>;
}

// Visual styling per step. Arrays are read modulo their length so they
// support chains of any size (2-5+).
const STEP_FILLS = ["#E9D5FF", "#DBEAFE", "#FCE7F3", "#DCFCE7", "#FFEDD5"];
const STEP_STROKES = ["#A855F7", "#3B82F6", "#EC4899", "#22C55E", "#F97316"];
const STEP_BORDERS = [
  "border-purple-300",
  "border-blue-300",
  "border-pink-300",
  "border-green-300",
  "border-orange-300",
];

const getStepDescription = (index: number, total: number): string => {
  if (index === 0) {
    return "Receives the user question and produces the first response.";
  }
  if (index === total - 1) {
    return "Produces the final answer shown to the user.";
  }
  return `Reads Assistant ${index}'s output and refines it.`;
};

const WorkflowDiagram: React.FC<WorkflowDiagramProps> = ({ chain }) => {
  const [zoom, setZoom] = useState(1);
  const svgRef = useRef<SVGSVGElement>(null);

  if (!chain || chain.length === 0) {
    return null;
  }

  // Layout: User Input | A1 | A2 | A3 | Final Output
  // Each box ~180w with ~40px arrow gap
  const boxWidth = 180;
  const arrowGap = 50;
  const boxHeight = 130;
  const startX = 20;

  const totalNodes = chain.length + 2; // user input + chain + final output
  const svgWidth = startX + totalNodes * boxWidth + (totalNodes - 1) * arrowGap + 20;
  const svgHeight = 240;
  const yCenter = svgHeight / 2;
  const yTop = yCenter - boxHeight / 2;

  const xFor = (index: number) => startX + index * (boxWidth + arrowGap);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => setZoom(1);

  const handleDownloadDiagram = () => {
    if (!svgRef.current) return;
    try {
      const svgClone = svgRef.current.cloneNode(true) as SVGElement;
      const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
      style.textContent = `
        text { font-family: system-ui, -apple-system, sans-serif; }
        .fill-gray-900 { fill: #111827; }
        .font-semibold { font-weight: 600; }
      `;
      svgClone.insertBefore(style, svgClone.firstChild);

      const svgData = new XMLSerializer().serializeToString(svgClone);
      const svgDataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgData);

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const scale = 2;
      canvas.width = svgWidth * scale;
      canvas.height = svgHeight * scale;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const img = new Image();
      img.onload = () => {
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, svgWidth, svgHeight);
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const firstName = chain[0]?.name?.replace(/\s+/g, "-").toLowerCase() || "workflow";
            link.download = `workflow-chain-${firstName}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success("Workflow diagram downloaded successfully");
          }
        }, "image/png");
      };
      img.onerror = () => toast.error("Failed to generate diagram image");
      img.src = svgDataUrl;
    } catch (error) {
      console.error("Error downloading diagram:", error);
      toast.error("Failed to download diagram");
    }
  };

  return (
    <div className="w-full">
      {/* Zoom Controls */}
      <div className="flex items-center justify-end gap-2 mb-2">
        <button
          type="button"
          onClick={handleDownloadDiagram}
          className="p-1.5 text-gray-600 hover:text-violet-600 hover:bg-violet-50 rounded transition-colors"
          title="Download Diagram"
        >
          <Download size={16} />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1"></div>
        <button
          type="button"
          onClick={handleZoomOut}
          className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          title="Zoom Out"
        >
          <ZoomOut size={16} />
        </button>
        <button
          type="button"
          onClick={handleResetZoom}
          className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          title="Reset Zoom"
        >
          <RotateCcw size={16} />
        </button>
        <button
          type="button"
          onClick={handleZoomIn}
          className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          title="Zoom In"
        >
          <ZoomIn size={16} />
        </button>
        <span className="text-xs text-gray-500 ml-2 min-w-[45px]">
          {Math.round(zoom * 100)}%
        </span>
      </div>

      {/* Diagram Container */}
      <div
        className="w-full overflow-auto border border-gray-200 rounded bg-white flex items-center justify-center"
        style={{ maxHeight: "600px" }}
      >
        <div
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: "center center",
            transition: "transform 0.2s ease",
          }}
        >
          <svg
            ref={svgRef}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            style={{ width: `${svgWidth}px`, height: `${svgHeight}px`, display: "block", margin: "0 auto" }}
          >
            {/* User Input box */}
            <g>
              <rect
                x={xFor(0)}
                y={yTop}
                width={boxWidth}
                height={boxHeight}
                rx="8"
                fill="#F0FDF4"
                stroke="#22C55E"
                strokeWidth="2"
              />
              <foreignObject x={xFor(0) + 10} y={yTop + 10} width={boxWidth - 20} height={boxHeight - 20}>
                <div className="flex flex-col h-full p-2">
                  <div className="font-semibold text-sm text-gray-900 text-center mb-2 border-b border-green-300 pb-1">
                    User Input
                  </div>
                  <div className="text-xs text-gray-700 leading-snug">
                    User question enters the chain
                  </div>
                </div>
              </foreignObject>
            </g>

            {/* Chain assistants */}
            {chain.map((step, i) => {
              const x = xFor(i + 1);
              const prevX = xFor(i) + boxWidth;
              return (
                <g key={`${step.id}-${i}`}>
                  {/* Arrow from previous box */}
                  <line
                    x1={prevX}
                    y1={yCenter}
                    x2={x}
                    y2={yCenter}
                    stroke="#6B7280"
                    strokeWidth="2"
                    markerEnd="url(#arrowhead)"
                  />
                  <rect
                    x={x}
                    y={yTop}
                    width={boxWidth}
                    height={boxHeight}
                    rx="8"
                    fill={STEP_FILLS[i % STEP_FILLS.length]}
                    stroke={STEP_STROKES[i % STEP_STROKES.length]}
                    strokeWidth="2"
                  />
                  <foreignObject x={x + 10} y={yTop + 10} width={boxWidth - 20} height={boxHeight - 20}>
                    <div className="flex flex-col h-full p-2">
                      <div className={`font-semibold text-sm text-gray-900 text-center mb-1 border-b ${STEP_BORDERS[i % STEP_BORDERS.length]} pb-1`}>
                        {`Assistant ${i + 1}: ${step.name}`}
                      </div>
                      <div className="text-xs text-gray-700 leading-snug">
                        {getStepDescription(i, chain.length)}
                      </div>
                    </div>
                  </foreignObject>
                </g>
              );
            })}

            {/* Final output arrow + box */}
            {(() => {
              const finalIndex = chain.length + 1;
              const finalX = xFor(finalIndex);
              const prevX = xFor(finalIndex - 1) + boxWidth;
              return (
                <g>
                  <line
                    x1={prevX}
                    y1={yCenter}
                    x2={finalX}
                    y2={yCenter}
                    stroke="#6B7280"
                    strokeWidth="2"
                    markerEnd="url(#arrowhead)"
                  />
                  <rect
                    x={finalX}
                    y={yTop}
                    width={boxWidth}
                    height={boxHeight}
                    rx="8"
                    fill="#FEF3C7"
                    stroke="#F59E0B"
                    strokeWidth="2"
                  />
                  <foreignObject x={finalX + 10} y={yTop + 10} width={boxWidth - 20} height={boxHeight - 20}>
                    <div className="flex flex-col h-full p-2">
                      <div className="font-semibold text-sm text-gray-900 text-center mb-2 border-b border-amber-300 pb-1">
                        Final Output
                      </div>
                      <div className="text-xs text-gray-700 leading-snug">
                        {`Assistant ${chain.length}'s response delivered to the user`}
                      </div>
                    </div>
                  </foreignObject>
                </g>
              );
            })()}

            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 10 3, 0 6" fill="#6B7280" />
              </marker>
            </defs>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default WorkflowDiagram;
